import { ThemeProvider } from "@emotion/react";
import {
  Box,
  CircularProgress,
  createTheme,
  CssBaseline,
  GlobalStyles,
  Snackbar,
  Stack,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import AdminPanel from "./AdminPanel";
import Header from "./Header";
import LoginPage from "./LoginPage";
import Main from "./Main";
import ProgressDialog from "./ProgressDialog";
import ShareLinksDialog from "./ShareLinksDialog";
import UploadProgressBar from "./UploadProgressBar";
import { fetchAuthState } from "./app/auth";
import { TransferQueueProvider } from "./app/transferQueue";

const globalStyles = (
  <GlobalStyles styles={{ "html, body, #root": { height: "100%" } }} />
);

const theme = createTheme({
  palette: { primary: { main: "#f38020" } },
});

const ADMIN_PATH = "/admin";

function App() {
  const [search, setSearch] = useState("");
  const [showProgressDialog, setShowProgressDialog] = React.useState(false);
  const [showShareLinksDialog, setShowShareLinksDialog] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [authed, setAuthed] = useState<boolean | null>(null);
  const [path, setPath] = useState(window.location.pathname);

  const checkAuth = useCallback(() => {
    fetchAuthState()
      .then((state) => setAuthed(state.authenticated))
      .catch(() => setAuthed(false));
  }, []);

  useEffect(checkAuth, [checkAuth]);

  // Keep the in-app route in step with the browser's back/forward buttons.
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string) => {
    window.history.pushState({}, "", to);
    setPath(to);
  }, []);

  let content: React.ReactNode;

  if (authed === null) {
    content = (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  } else if (!authed) {
    content = <LoginPage onSignedIn={() => setAuthed(true)} />;
  } else if (path === ADMIN_PATH) {
    content = <AdminPanel onExit={() => navigate("/")} />;
  } else {
    content = (
      <Stack sx={{ height: "100%" }}>
        <Header
          search={search}
          onSearchChange={(newSearch: string) => setSearch(newSearch)}
          setShowProgressDialog={setShowProgressDialog}
          setShowShareLinksDialog={setShowShareLinksDialog}
          onOpenAdmin={() => navigate(ADMIN_PATH)}
          onSignedOut={() => setAuthed(false)}
        />
        <Main search={search} onError={setError} />
      </Stack>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {globalStyles}
      <TransferQueueProvider>
        {content}
        {authed && <UploadProgressBar onExpand={() => setShowProgressDialog(true)} />}
        <Snackbar
          autoHideDuration={5000}
          open={Boolean(error)}
          message={error?.message}
          onClose={() => setError(null)}
        />
        <ProgressDialog
          open={showProgressDialog}
          onClose={() => setShowProgressDialog(false)}
        />
        <ShareLinksDialog
          open={showShareLinksDialog}
          onClose={() => setShowShareLinksDialog(false)}
        />
      </TransferQueueProvider>
    </ThemeProvider>
  );
}

export default App;
